import React, {useState} from 'react';

import {useCurrentUserState} from 'app/current-user/api';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {ActionButton, SPPage} from 'app/general/structures/SPPage';
import {ModernLinkTabs} from 'app/general/widgets/ModernTabs';
import {sweetAPI} from 'app/store/sweet-api';
import {useModelChangeSubscription} from 'app/subscriptions';
import {AssignNewTaskModal} from 'app/tasks/components/AssignTaskModal';
import {TASKS_HOME} from 'app/urls';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';
import {TASKS_RTK_TAGS} from 'app/utils/rtk-tags';

import {AllTasksList} from './AllTasksList';
import {BulkSelectTasksProvider} from './BulkSelectTasksContext';
import {MyTasksList} from './MyTasksList';

import type {BreadcrumbProps} from 'app/general/structures/BreadcrumbItemStructure';

export function TasksDashboard(): JSX.Element {
  const userTimezone = useCurrentUserState().data!.timezone;
  const [showingAssignModal, showAssignModal] = useState(false);

  const actions = useAsyncActions({
    invalidateTags: sweetAPI.util.invalidateTags,
  });

  const toggleAssignTaskModal = () => showAssignModal((show) => !show);

  useModelChangeSubscription((message, {runWhenAny, anyModelMatch}) => {
    runWhenAny(message, [anyModelMatch(['taskinstance', 'tasktemplate'])], () =>
      refreshTasks()
    );
  }, []);

  const refreshTasks = () => {
    return Promise.resolve(
      actions.invalidateTags([
        // @ts-expect-error we inject tags on the API after it's created so TS doesn't know bout them
        {type: TASKS_RTK_TAGS.SLIMINSTANCE, id: 'list'},
      ])
    );
  };

  return (
    <SPPage
      pageTitle={gt('Tasks')}
      actionArea={
        <ActionButton
          size="sm"
          text={gt('Assign Task')}
          icon="fa-plus-circle"
          onClick={toggleAssignTaskModal}
        />
      }
    >
      {({canAssignTasks}) => (
        <>
          <ModernLinkTabs
            tabs={[
              {
                label: gt('My Tasks'),
                to: '/tasks/mine/',
                content: () => (
                  <BulkSelectTasksProvider
                    key="my-tasks"
                    enableBulkSelect={false}
                  >
                    <MyTasksList />
                  </BulkSelectTasksProvider>
                ),
              },
              {
                label: gt('All Tasks'),
                to: '/tasks/all/',
                content: () => (
                  <BulkSelectTasksProvider
                    key="all-tasks"
                    enableBulkSelect={canAssignTasks}
                  >
                    <AllTasksList
                      isManager={canAssignTasks}
                      userTimezone={userTimezone}
                      toggleAssignTaskModal={toggleAssignTaskModal}
                    />
                  </BulkSelectTasksProvider>
                ),
              },
            ]}
          />
          <AssignNewTaskModal
            show={showingAssignModal}
            onToggle={toggleAssignTaskModal}
          />
        </>
      )}
    </SPPage>
  );
}

export const TasksPageBreadcrumb = (props: BreadcrumbProps): JSX.Element => (
  <BreadcrumbItemStructure
    {...props}
    text={gt('Tasks')}
    icon="fa-tasks"
    link={TASKS_HOME}
  />
);
